import { useEffect, useRef, useState } from "react";

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="font-display text-lg font-bold mb-3">{title}</div>
        )}
        <div className="text-sm text-ink/80">{children}</div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl bg-wa px-5 py-2 text-sm font-semibold text-white hover:bg-wa-dark transition"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export function Avatar({ color, size = 36, ring }: { color: string; size?: number; ring?: boolean }) {
  return (
    <span
      className={"inline-block rounded-full shrink-0 " + (ring ? "ring-2 ring-white" : "border border-black/10")}
      style={{ backgroundColor: color, width: size, height: size }}
    />
  );
}

/** Poll a fetcher on an interval; returns the latest value and a manual refresh. */
export function usePoll<T>(fetcher: () => Promise<T>, ms: number, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fnRef = useRef(fetcher);
  fnRef.current = fetcher;

  async function refresh() {
    try {
      const r = await fnRef.current();
      setData(r);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fnRef.current();
        if (alive) {
          setData(r);
          setError(null);
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    const t = setInterval(refresh, ms);
    return () => {
      alive = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, refresh };
}

/** Seconds remaining until an ISO timestamp, ticking every second. */
export function useCountdown(endsAtIso: string | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endsAtIso) return 0;
  return Math.max(0, Math.round((new Date(endsAtIso).getTime() - now) / 1000));
}

export function mmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
